/**
 * FeedbacksPageV2 — redesigned "User Feedback" page.
 *
 * Layout: PageHeader + LibTable with server-side pagination and a
 * client-debounced search field (300 ms). Session column click
 * opens a Modal with parent type / name + session_id.
 *
 * Same export endpoint as v1 — exportFeedbacksDataCSV honors the
 * current search filter.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchFeedbacks } from '../../../../actions/feedbacksActions';
import { exportFeedbacksDataCSV } from '../../../../actions/exportCSVActions';
import LoadingBackdrop from '../../../Common/LoadingBackdrop';
import {
  DEFAULT_PER_PAGE,
  PARSE_INT_RADIX,
} from '../../../../constants/pagination';
import {
  PageHeader,
  Button,
  LibTable,
  Modal,
} from '../../../redesign';
import { Pager } from '../../RequestsPage/v2/RequestsPageV2';

const SearchIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 14, height: 14 }}>
    <circle cx="11" cy="11" r="7" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);
const DownloadIcon = (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7 10 12 15 17 10" />
    <line x1="12" y1="15" x2="12" y2="3" />
  </svg>
);
const VideoIcon = (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="23 7 16 12 23 17 23 7" />
    <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
  </svg>
);
const AudioIcon = (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 18V5l12-2v13" />
    <circle cx="6" cy="18" r="3" />
    <circle cx="18" cy="16" r="3" />
  </svg>
);

function formatDateTime(epochSeconds) {
  const dt = new Date(epochSeconds * 1000);
  return {
    date: dt.toLocaleDateString(),
    time: dt.toLocaleTimeString(),
  };
}

export default function FeedbacksPageV2() {
  const dispatch = useDispatch();
  const { feedbacks: { data = [], pagination = { total: 0, page: 1, per_page: DEFAULT_PER_PAGE, total_pages: 1 } }, isLoading } = useSelector((s) => s.feedbacks);

  const [searchText, setSearchText] = useState('');
  const searchTimeoutRef = useRef(null);

  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsFeedback, setDetailsFeedback] = useState(null);

  // Debounce the search input → server fetch.
  useEffect(() => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      dispatch(fetchFeedbacks(1, pagination.per_page, searchText || undefined));
    }, searchText ? 300 : 0);
    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
    // pagination.per_page captured deliberately so a page-size change
    // re-runs from page 1; same behaviour as v1.
  }, [dispatch, searchText, pagination.per_page]);

  const handlePrev = () => {
    if (pagination.page <= 1) return;
    dispatch(fetchFeedbacks(pagination.page - 1, pagination.per_page, searchText || undefined));
  };
  const handleNext = () => {
    if (pagination.page >= pagination.total_pages) return;
    dispatch(fetchFeedbacks(pagination.page + 1, pagination.per_page, searchText || undefined));
  };
  const handlePerPage = (e) => {
    dispatch(fetchFeedbacks(1, parseInt(e.target.value, PARSE_INT_RADIX), searchText || undefined));
  };

  const handleExport = () =>
    dispatch(exportFeedbacksDataCSV({ search: searchText || undefined }));

  const openDetails = (feedback) => {
    setDetailsFeedback(feedback);
    setDetailsOpen(true);
  };

  return (
    <div className="bg-bg min-h-full">
      <LoadingBackdrop open={!!isLoading} />
      <PageHeader
        eyebrow="Users"
        title="User Feedback"
        total={
          pagination.total != null
            ? `${pagination.total.toLocaleString()} total`
            : undefined
        }
        actions={
          <Button
            variant="ghost"
            leadingIcon={DownloadIcon}
            onClick={handleExport}
          >
            Export CSV
          </Button>
        }
      />

      <div style={{ padding: '0 28px 32px' }}>
        <div
          className="flex items-center"
          style={{ gap: 12, padding: '0 4px 12px' }}
        >
          <div
            className="flex items-center bg-cream border border-sand"
            style={{ borderRadius: 8, padding: '0 12px', gap: 8, flex: 1, maxWidth: 480 }}
          >
            <span className="text-text-muted shrink-0">{SearchIcon}</span>
            <input
              type="text"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="Search by message, user, or session…"
              style={{
                flex: 1,
                minWidth: 0,
                border: 0,
                background: 'transparent',
                outline: 'none',
                fontFamily: 'inherit',
                fontSize: 13,
                color: '#2C3E50',
                padding: '8px 0',
              }}
            />
            {searchText && (
              <button
                type="button"
                onClick={() => setSearchText('')}
                className="text-text-muted shrink-0"
                style={{
                  background: 'transparent',
                  border: 0,
                  cursor: 'pointer',
                  fontSize: 13,
                  fontFamily: 'inherit',
                  padding: 4,
                }}
                aria-label="Clear search"
              >
                ×
              </button>
            )}
          </div>
        </div>

        {data.length === 0 ? (
          <div
            className="bg-surface border border-sand text-text-muted italic text-center"
            style={{ borderRadius: 12, padding: '40px 16px', fontSize: 13 }}
          >
            {searchText
              ? 'No feedback matches your search.'
              : isLoading
                ? 'Loading…'
                : 'No feedback yet.'}
          </div>
        ) : (
          <>
            <LibTable
              fixed
              className="border border-sand"
              style={{ borderRadius: 12, overflow: 'hidden' }}
            >
              <colgroup>
                {/* message */}
                <col />
                {/* user */}
                <col style={{ width: '20%' }} />
                {/* session */}
                <col style={{ width: '24%' }} />
                {/* parent type */}
                <col style={{ width: 140 }} />
                {/* date */}
                <col style={{ width: 160 }} />
              </colgroup>
              <LibTable.Head>
                <tr>
                  <LibTable.Th>Message</LibTable.Th>
                  <LibTable.Th>User</LibTable.Th>
                  <LibTable.Th>Session</LibTable.Th>
                  <LibTable.Th>Parent</LibTable.Th>
                  <LibTable.Th>Date &amp; Time</LibTable.Th>
                </tr>
              </LibTable.Head>
              <tbody>
                {data.map((f) => {
                  const { date, time } = formatDateTime(f.timestamp);
                  return (
                    <LibTable.Row key={f.id}>
                      <LibTable.Td>
                        <div
                          className="text-navy"
                          style={{
                            fontSize: 13,
                            lineHeight: 1.4,
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                          }}
                        >
                          {f.feedback_message || '—'}
                        </div>
                      </LibTable.Td>
                      <LibTable.Td>
                        <div className="text-navy" style={{ fontSize: 13, fontWeight: 600 }}>
                          {f.user_full_name || '—'}
                        </div>
                        <div className="font-mono text-text-muted truncate" style={{ fontSize: 11 }} title={f.user_email}>
                          {f.user_email}
                        </div>
                      </LibTable.Td>
                      <LibTable.Td>
                        <button
                          type="button"
                          onClick={() => openDetails(f)}
                          className="flex items-center text-navy"
                          style={{
                            gap: 6,
                            background: 'transparent',
                            border: 0,
                            padding: 0,
                            cursor: 'pointer',
                            fontFamily: 'inherit',
                            fontSize: 13,
                            textAlign: 'left',
                            textDecoration: 'underline',
                            textUnderlineOffset: 2,
                          }}
                        >
                          <span className="text-text-muted shrink-0">
                            {f.is_quick_tap ? VideoIcon : AudioIcon}
                          </span>
                          <span style={{ wordBreak: 'break-word' }}>
                            {f.session_name || '—'}
                          </span>
                        </button>
                      </LibTable.Td>
                      <LibTable.Td>
                        <span className="text-navy" style={{ fontSize: 12 }}>
                          {f.parent_type || '—'}
                        </span>
                        {f.parent_name && (
                          <div
                            className="text-text-muted truncate"
                            style={{ fontSize: 11, marginTop: 2 }}
                            title={f.parent_name}
                          >
                            {f.parent_name}
                          </div>
                        )}
                      </LibTable.Td>
                      <LibTable.Td>
                        <div className="text-navy" style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                          {date}
                        </div>
                        <div className="font-mono text-text-muted" style={{ fontSize: 11, whiteSpace: 'nowrap' }}>
                          {time}
                        </div>
                      </LibTable.Td>
                    </LibTable.Row>
                  );
                })}
              </tbody>
            </LibTable>

            <Pager
              pagination={pagination}
              onPrev={handlePrev}
              onNext={handleNext}
              onPerPage={handlePerPage}
            />
          </>
        )}
      </div>

      <Modal
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        eyebrow="Session"
        title={detailsFeedback?.session_name || 'Session details'}
        maxWidth={560}
        footer={
          <Button variant="ghost" onClick={() => setDetailsOpen(false)}>
            Close
          </Button>
        }
      >
        {detailsFeedback && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <DetailRow
              label="Session"
              icon={detailsFeedback.is_quick_tap ? VideoIcon : AudioIcon}
              value={detailsFeedback.session_name || '—'}
            />
            <DetailRow
              label="Session ID"
              value={detailsFeedback.session_id ?? '—'}
              mono
            />
            <DetailRow
              label="Parent type"
              value={detailsFeedback.parent_type || '—'}
            />
            <DetailRow
              label="Parent name"
              value={detailsFeedback.parent_name || '—'}
            />
          </div>
        )}
      </Modal>
    </div>
  );
}

function DetailRow({ label, value, icon, mono }) {
  return (
    <div>
      <div
        className="font-condensed font-bold uppercase text-text-muted"
        style={{ fontSize: 10.5, letterSpacing: '0.08em', marginBottom: 4 }}
      >
        {label}
      </div>
      <div
        className={mono ? 'font-mono' : ''}
        style={{
          fontSize: mono ? 12 : 13.5,
          color: '#2C3E50',
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          wordBreak: 'break-word',
        }}
      >
        {icon && <span className="text-text-muted shrink-0">{icon}</span>}
        <span>{value}</span>
      </div>
    </div>
  );
}
